import { FC } from 'react';
import { Control, UseFormResetField, UseFormSetValue, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { ListIcon } from '../../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../../components/app-formatted-message/index';
import { EmptySearchList } from '../../../../../../../components/empty-search-list/index';
import Loader from '../../../../../../../components/loader/index';
import {
  SharePlan as SharePlanHooks,
  useLocale,
  ValuationHooks,
} from '../../../../../../../hooks/index';
import { SharePlan } from '../../../../../../../types/pool-plans.types';
import { toNumber } from '../../../../../../../utils/to-number.util';
import { toPlural } from '../../../../../../../utils/to-plural.util';
import { ExerciseModalItem } from '../../../exercise-modal';
import { FormSchema } from '../../exercise-form.validation';
import { Hurdle } from './hurdle';

export type ApproveHurdlePlanListProps = {
  companyId: string;
  control: Control<FormSchema>;
  resetField: UseFormResetField<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
};

type ApproveHurdlePlanListItemProps = {
  companyId: string;
  setSelectedHurdlePlanId: (id: string) => void;
  sharePlan: SharePlan;
};

const ApproveHurdlePlanListItem: FC<ApproveHurdlePlanListItemProps> = ({
  companyId,
  sharePlan: { id, hardleValue, name },
  setSelectedHurdlePlanId,
}) => {
  const { approveHurdleGrants: approveHurdleGrantsData } = SharePlanHooks.useSharePlanGrantItems({
    companyId,
    sharePlanId: id,
  });

  const approveHurdleGrants = approveHurdleGrantsData.filter(
    ({ vestedSharesCount, exercisedCount }) => vestedSharesCount - exercisedCount > 0,
  );

  return (
    <ExerciseModalItem
      description={
        <>
          <span className="text-nowrap">
            <AppFormattedMessage id="HURDLE_VALUE" />: ${hardleValue?.toLocaleString('en-US')}
          </span>
          <span className="size-[3px] shrink-0 rounded-full bg-gray-300" />
          <span>
            {approveHurdleGrants.length} {toPlural('grant', approveHurdleGrants.length > 1)}{' '}
            <AppFormattedMessage id="ASSOCIATED_TO_THIS_PLAN" />
          </span>
        </>
      }
      onClick={() => setSelectedHurdlePlanId(id)}
      title={name}
    >
      <ListIcon className="-scale-x-100" />
    </ExerciseModalItem>
  );
};

export const ApproveHurdlePlanList: FC<ApproveHurdlePlanListProps> = ({
  companyId,
  setFormData,
  control,
  resetField,
}) => {
  const { stepOne } = useWatch<FormSchema>({ control });
  const { messagesLocale } = useLocale();
  const { approveHurdleSharePlans, isLoading } = SharePlanHooks.useApproveHurdleSharePlans({
    companyId,
  });
  const { valuation, isLoading: isValuationLoading } = ValuationHooks.useCurrent();
  if (isLoading || isValuationLoading) return <Loader className="h-[100px]" />;

  if (!stepOne?.hurdle?.planId) {
    const hasPlans = approveHurdleSharePlans.length > 0;
    const showEmpty = !valuation || !hasPlans;

    return (
      <>
        {valuation && hasPlans && (
          <span className="pb-7 text-label-md font-medium text-gray-400">
            <AppFormattedMessage id="CURRENT_VALUATION" />:{' '}
            {toNumber(
              toNumber(valuation.issuedShares || valuation.issuedSharesOnStart) *
                valuation.sharePrice,
            ).toLocaleString('en-US')}
          </span>
        )}
        <div
          className={twMerge(
            'invisible-y-scroll flex h-fit max-h-[150px] flex-col divide-y divide-gray-200 rounded-xl bg-gray-50 px-4',
            showEmpty && 'centered max-h-[350px]',
          )}
        >
          {!valuation ? (
            <EmptySearchList
              className="my-10"
              customDescriptionText={messagesLocale['NO_VALUATION_EVENTS_YET']}
              customTitleText={<AppFormattedMessage id="VALUATION_REQUIRED" />}
            />
          ) : !hasPlans ? (
            <EmptySearchList
              className="my-10"
              customDescriptionText=""
              customTitleText={<AppFormattedMessage id="NO_PLANS_TO_APPROVE" />}
            />
          ) : (
            approveHurdleSharePlans.map((sharePlan) => (
              <ApproveHurdlePlanListItem
                companyId={companyId}
                key={sharePlan.id}
                setSelectedHurdlePlanId={(id) => setFormData('stepOne.hurdle.planId', id)}
                sharePlan={sharePlan}
              />
            ))
          )}
        </div>
      </>
    );
  }

  if (!valuation) {
    return (
      <div
        className={twMerge(
          'invisible-y-scroll flex h-fit flex-col rounded-xl bg-gray-50 px-4',
          'centered max-h-[350px]',
        )}
      >
        <EmptySearchList
          className="my-10"
          customDescriptionText={messagesLocale['NO_VALUATION_EVENTS_YET']}
          customTitleText={<AppFormattedMessage id="VALUATION_REQUIRED" />}
        />
      </div>
    );
  }

  return (
    <Hurdle
      companyId={companyId}
      control={control}
      resetField={resetField}
      setFormData={setFormData}
      valuation={valuation}
    />
  );
};
